// Entity-code arithmetic. The geographic hierarchy is encoded in the code itself:
// `13` (state) → `13-16` (LGA) → `13-16-12` (ward) → `13-16-12-001` (polling unit).
// Level = dash-segment count, which is also how api.core infers it.

const SEPARATOR = '-';

export const segmentCount = (code: string): number => code.split(SEPARATOR).length;

const isWellFormed = (code: string): boolean => {
	if (!code?.trim()) return false;
	const segments = code.split(SEPARATOR);
	return segments.length >= 1 && segments.length <= 4 && segments.every((s) => s.trim().length > 0);
};

/**
 * The code one level up: `13-16-12-001` → `13-16-12`. Null for a state code, which has no
 * parent, and for anything malformed — a caller must not build a lookup from garbage.
 */
export const parentCodeOf = (code: string | null | undefined): string | null => {
	if (!code || !isWellFormed(code)) return null;
	const segments = code.split(SEPARATOR);
	if (segments.length < 2) return null;
	return segments.slice(0, -1).join(SEPARATOR);
};

/** Plain-language level name for a code, by segment count. */
export const levelLabelOfCode = (code: string): string => levelLabelOfSegments(segmentCount(code));

const levelLabelOfSegments = (segments: number): string => {
	switch (segments) {
		case 1:
			return 'State';
		case 2:
			return 'LGA';
		case 3:
			return 'Ward';
		default:
			return 'Polling unit';
	}
};

/**
 * What to call the agent one level UP from a result type: a polling-unit agent (ec8a) has a
 * "ward" agent, a ward agent (ec8b) an "LGA" agent, an LGA agent (ec8c) a "state" agent. A
 * state agent (ec8d) has nobody above them in the app, so null.
 */
export const parentLevelLabel = (resultType: string | null | undefined): string | null => {
	switch ((resultType ?? '').toLowerCase()) {
		case 'ec8a':
			return 'ward';
		case 'ec8b':
			return 'LGA';
		case 'ec8c':
			return 'state';
		default:
			return null;
	}
};

/** The api.core `level` name a hierarchy node carries, from a code. */
export const hierarchyLevelOfCode = (code: string): 'State' | 'LGA' | 'Ward' | 'PollingUnit' => {
	switch (segmentCount(code)) {
		case 1:
			return 'State';
		case 2:
			return 'LGA';
		case 3:
			return 'Ward';
		default:
			return 'PollingUnit';
	}
};
